import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import { getPublicResources } from '../services/resourceService';
import { getSavedResources, saveResource, unsaveResource } from '../utils/syncService';
import { markResourceAsSeen } from '../utils/notificationManager';

const ResourceHub = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isAdmin, setIsAdmin] = useState(() => {
    try { return localStorage.getItem('isAdmin') === 'true'; } catch { return false; }
  });
  const [savedResources, setSavedResources] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Load saved resources from Supabase
  useEffect(() => {
    const loadSavedResources = async () => {
      const userEmail = localStorage.getItem('userEmail');
      if (!userEmail) return;

      try {
        const result = await getSavedResources(userEmail);
        if (result.success) {
          setSavedResources(result.data);
        }
      } catch (error) {
        console.error('Error loading saved resources:', error);
      }
    };

    loadSavedResources();
  }, []);

  // Admin mode: parse ?admin=on/off and persist to localStorage
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
      const adminParam = params.get('admin');
      if (adminParam === 'on') {
        localStorage.setItem('isAdmin', 'true');
        setIsAdmin(true);
      } else if (adminParam === 'off') {
        localStorage.setItem('isAdmin', 'false');
        setIsAdmin(false);
      } else {
        const stored = localStorage.getItem('isAdmin');
        if (stored === 'true') setIsAdmin(true);
      }
    } catch {}
  }, []);

  const categories = [
    { id: 'all', name: 'All Resources', icon: '📚', color: 'bg-purple-500', gradient: 'from-purple-100 to-purple-50' },
    { id: 'saved', name: 'Saved', icon: '🔖', color: 'bg-yellow-500', gradient: 'from-yellow-100 to-yellow-50' },
    { id: 'eczema', name: 'Eczema Relief', icon: '✨', color: 'bg-green-500', gradient: 'from-green-100 to-green-50' },
    { id: 'allergies', name: 'Allergy Management', icon: '🌿', color: 'bg-blue-500', gradient: 'from-blue-100 to-blue-50' },
    { id: 'lifestyle', name: 'Lifestyle Tips', icon: '🌱', color: 'bg-teal-500', gradient: 'from-teal-100 to-teal-50' },
    { id: 'products', name: 'Recipe Books', icon: '📖', color: 'bg-pink-500', gradient: 'from-pink-100 to-pink-50' },
    { id: 'other', name: 'Other', icon: '💫', color: 'bg-gray-500', gradient: 'from-gray-100 to-gray-50' }
  ];

  const defaultResources = [
    {
      id: 1,
      title: 'Getting Started with Alera Resources',
      category: 'lifestyle',
      type: 'guide',
      description: 'Welcome to the Resource Hub! This is your space to explore wellness guides, personal stories, research, and practical tips.',
      readTime: '2 min read',
      downloadable: false,
      featured: true,
      author: 'By Kate',
      tags: ['welcome', 'getting-started'],
      image: '📚'
    }
  ];

  const [resources, setResources] = useState([]);

  // Load resources from Supabase
  useEffect(() => {
    const loadResources = async () => {
      try {
        const result = await getPublicResources();
        if (result.success && result.data) {
          // Convert Supabase format to display format
          const formattedResources = result.data.map(r => ({
            id: r.id,
            title: r.title,
            description: r.description,
            category: r.category,
            type: r.type,
            image: r.image_url || '📄',
            content: r.content,
            author: r.author,
            tags: r.tags || [],
            readTime: r.read_time,
            featured: r.featured,
            link: r.link
          }));
          setResources(formattedResources);
          // Mark all loaded resources as seen for new resource notifications
          formattedResources.forEach(r => markResourceAsSeen(r.id));
        }
      } catch (error) {
        console.error('Error loading resources:', error);
      }
    };

    loadResources();

    // Listen for updates from admin dashboard
    const handleResourcesUpdated = () => {
      loadResources();
    };
    window.addEventListener('resourcesUpdated', handleResourcesUpdated);
    return () => window.removeEventListener('resourcesUpdated', handleResourcesUpdated);
  }, []);

  const toggleSaveResource = async (resourceId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const userEmail = localStorage.getItem('userEmail');
    if (!userEmail) {
      console.warn('No user email found');
      return;
    }

    if (savedResources.includes(resourceId)) {
      // Remove from saved
      setSavedResources(savedResources.filter(id => id !== resourceId));
      await unsaveResource(userEmail, resourceId);
    } else {
      // Add to saved
      setSavedResources([...savedResources, resourceId]);
      await saveResource(userEmail, resourceId);
    }
  };

  const filteredResources = resources.filter(resource => {
    // Handle 'saved' category
    if (activeCategory === 'saved') {
      const isSaved = savedResources.includes(resource.id);
      const matchesSearch = resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           resource.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           resource.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
      return isSaved && matchesSearch;
    }
    
    // Handle other categories
    const matchesCategory = activeCategory === 'all' || resource.category === activeCategory;
    const matchesSearch = resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         resource.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         resource.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const featuredResources = resources.filter(resource => resource.featured);

  const getTypeIcon = (type) => {
    const icons = {
      article: '📄',
      guide: '📋',
      checklist: '✅',
      ebook: '📖',
      video: '🎥',
      story: '💭',
      research: '🔬',
      tip: '💡'
    };
    return icons[type] || '📄';
  };

  const getTypeColor = (type) => {
    const colors = {
      article: 'bg-blue-100 text-blue-700',
      guide: 'bg-green-100 text-green-700',
      checklist: 'bg-purple-100 text-purple-700',
      ebook: 'bg-orange-100 text-orange-700',
      video: 'bg-red-100 text-red-700',
      story: 'bg-pink-100 text-pink-700',
      research: 'bg-indigo-100 text-indigo-700',
      tip: 'bg-yellow-100 text-yellow-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-50 pt-20 px-3 sm:px-6 pb-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-start gap-3 mb-8">
          <RouterLink
            to="/dashboard"
            className="text-gray-600 hover:text-gray-800 transition-colors mt-1 min-h-[44px] flex items-center"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </RouterLink>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-charcoal-800">Wellness Hub</h1>
              {isAdmin && (
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">Admin</span>
                  <button
                    onClick={() => { localStorage.setItem('isAdmin','false'); setIsAdmin(false); }}
                    className="bg-gray-200 text-charcoal-700 px-3 py-1.5 rounded-lg hover:bg-gray-300 transition-colors text-sm min-h-[44px]"
                  >
                    Exit Admin
                  </button>
                </div>
              )}
            </div>
            <p className="text-sm sm:text-base text-charcoal-600">Your comprehensive library of wellness guides, tips, and tools</p>
          </div>
        </div>

        {/* Filters & Search */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          {/* Category Filter Dropdown */}
          <div className="relative flex-shrink-0 w-full sm:w-56">
            <select
              value={activeCategory}
              onChange={(e) => setActiveCategory(e.target.value)}
              className="w-full appearance-none pl-10 pr-10 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm bg-white cursor-pointer"
            >
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.icon} {category.name}
                </option>
              ))}
            </select>
            <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>

          {/* Search Bar */}
          <div className="relative w-full sm:w-64">
            <input
              type="text"
              placeholder="Search resources..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
            />
            <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        {/* Featured Resources */}
        {activeCategory === 'all' && featuredResources.length > 0 && (
          <div className="mb-12">
            <h2 className="text-xl sm:text-2xl font-semibold text-charcoal-800 mb-6">Featured Resources</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredResources.map((resource) => (
                <RouterLink
                  key={resource.id}
                  to={resource.content && String(resource.content).trim().length > 0 ? `/resources/${resource.id}` : resource.link || '#'}
                  target={resource.link && !resource.content ? '_blank' : undefined}
                  rel={resource.link && !resource.content ? 'noopener noreferrer' : undefined}
                  className="block"
                >
                  <motion.div
                    className="bg-white rounded-xl shadow-sm border border-purple-100 overflow-hidden hover:shadow-lg transition-all cursor-pointer h-full flex flex-col"
                    whileHover={{ y: -4, scale: 1.02 }}
                  >
                    {/* Image/Video Thumbnail */}
                    <div className="relative h-48 sm:h-56 bg-gradient-to-br from-purple-50 to-pink-50 overflow-hidden">
                      {resource.image ? (
                        (resource.image.startsWith('data:') || resource.image.startsWith('http')) ? (
                          <img src={resource.image} alt={resource.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-6xl">{resource.image}</div>
                        )
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-6xl text-gray-300">📄</div>
                      )}
                      {/* Video Play Indicator */}
                      {resource.type === 'video' && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20">
                          <div className="bg-white rounded-full p-4 shadow-lg">
                            <svg className="w-8 h-8 text-purple-600" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </div>
                        </div>
                      )}
                      {/* Save/Favorite Button */}
                      <button
                        onClick={(e) => toggleSaveResource(resource.id, e)}
                        className="absolute top-3 right-3 bg-white rounded-full p-2 shadow-lg hover:scale-110 transition-transform z-10"
                        aria-label={savedResources.includes(resource.id) ? 'Unsave resource' : 'Save resource'}
                      >
                        <svg className="w-5 h-5" fill={savedResources.includes(resource.id) ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" className={savedResources.includes(resource.id) ? 'text-purple-600' : 'text-gray-600'} />
                        </svg>
                      </button>
                    </div>
                    <div className="p-4 sm:p-6 flex-1 flex flex-col">
                      <div className="flex items-center justify-between mb-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getTypeColor(resource.type)}`}>
                          {getTypeIcon(resource.type)} {resource.type}
                        </span>
                      </div>
                      
                      <h3 className="text-lg font-semibold text-charcoal-800 mb-2">{resource.title}</h3>
                      <p className="text-charcoal-600 text-sm mb-4 flex-1">{resource.description}</p>
                      
                      {/* Author & Read Time */}
                      <div className="flex items-center justify-between text-xs text-charcoal-500 mb-3 pb-3 border-t pt-3">
                        <div className="flex items-center gap-2">
                          {resource.author && (
                            <span className="flex items-center gap-1">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              {resource.author}
                            </span>
                          )}
                        </div>
                        {resource.readTime && (
                          <span>{resource.readTime}</span>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-end">
                        <span className="text-purple-600 text-sm font-medium flex items-center gap-1">
                          {resource.link && !resource.content ? (
                            <>
                              View
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </>
                          ) : resource.type === 'video' ? (
                            'Watch →'
                          ) : (
                            'Read →'
                          )}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                </RouterLink>
              ))}
            </div>
          </div>
        )}

        {/* All Resources */}
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold text-charcoal-800 mb-6">
            {activeCategory === 'all' ? 'All Resources' : categories.find(c => c.id === activeCategory)?.name}
            <span className="text-base sm:text-lg text-charcoal-500 ml-2">({filteredResources.length})</span>
          </h2>
          
          {filteredResources.length === 0 ? (
            <div className="bg-white rounded-lg p-8 sm:p-12 text-center">
              <div className="text-5xl sm:text-6xl mb-4">🔍</div>
              <h3 className="text-lg sm:text-xl font-semibold text-charcoal-800 mb-2">No resources found</h3>
              <p className="text-sm sm:text-base text-charcoal-600 mb-4">
                {searchTerm ? `No results for "${searchTerm}"` : 'No resources in this category yet'}
              </p>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors font-medium min-h-[44px]"
                >
                  Clear Search
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredResources.map((resource) => (
                <RouterLink
                  key={resource.id}
                  to={resource.content && String(resource.content).trim().length > 0 ? `/resources/${resource.id}` : resource.link || '#'}
                  target={resource.link && !resource.content ? '_blank' : undefined}
                  rel={resource.link && !resource.content ? 'noopener noreferrer' : undefined}
                  className="block"
                >
                  <motion.div
                    className="bg-white rounded-xl shadow-sm border border-purple-100 overflow-hidden hover:shadow-lg transition-all cursor-pointer h-full flex flex-col"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    whileHover={{ y: -4, scale: 1.02 }}
                  >
                    {/* Image/Video Thumbnail */}
                    <div className="relative h-48 sm:h-56 bg-gradient-to-br from-purple-50 to-pink-50 overflow-hidden">
                      {resource.image ? (
                        (resource.image.startsWith('data:') || resource.image.startsWith('http')) ? (
                          <img src={resource.image} alt={resource.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-6xl">{resource.image}</div>
                        )
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-6xl text-gray-300">📄</div>
                      )}
                      {/* Video Play Indicator */}
                      {resource.type === 'video' && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20">
                          <div className="bg-white rounded-full p-4 shadow-lg">
                            <svg className="w-8 h-8 text-purple-600" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </div>
                        </div>
                      )}
                      {/* Featured Badge */}
                      {resource.featured && (
                        <div className="absolute top-3 left-3">
                          <span className="bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                            ⭐ Featured
                          </span>
                        </div>
                      )}
                      {/* Save/Favorite Button */}
                      <button
                        onClick={(e) => toggleSaveResource(resource.id, e)}
                        className="absolute top-3 right-3 bg-white rounded-full p-2 shadow-lg hover:scale-110 transition-transform z-10"
                        aria-label={savedResources.includes(resource.id) ? 'Unsave resource' : 'Save resource'}
                      >
                        <svg className="w-5 h-5" fill={savedResources.includes(resource.id) ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" className={savedResources.includes(resource.id) ? 'text-purple-600' : 'text-gray-600'} />
                        </svg>
                      </button>
                    </div>
                    <div className="p-5 sm:p-6 flex-1 flex flex-col">
                      <div className="flex items-center justify-between mb-3">
                        <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${getTypeColor(resource.type)}`}>
                          {getTypeIcon(resource.type)} {resource.type}
                        </span>
                      </div>
                      
                      <h3 className="text-lg sm:text-xl font-semibold text-charcoal-800 mb-2 line-clamp-2">{resource.title}</h3>
                      <p className="text-charcoal-600 text-sm sm:text-base mb-4 flex-1 line-clamp-3">{resource.description}</p>
                      
                      {/* Tags */}
                      {resource.tags && resource.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-4">
                          {resource.tags.slice(0, 3).map((tag, index) => (
                            <span key={index} className="bg-gray-100 text-gray-600 px-2.5 py-1 rounded text-xs">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {/* Author & Read Time */}
                      <div className="flex items-center justify-between text-xs text-charcoal-500 mb-3 pb-3 border-t pt-3">
                        <div className="flex items-center gap-2">
                          {resource.author && (
                            <span className="flex items-center gap-1">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              {resource.author}
                            </span>
                          )}
                        </div>
                        {resource.readTime && (
                          <span>{resource.readTime}</span>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-end">
                        <span className="text-purple-600 text-sm font-medium flex items-center gap-1">
                          {resource.link && !resource.content ? (
                            <>
                              View
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </>
                          ) : resource.type === 'video' ? (
                            'Watch →'
                          ) : (
                            'Read →'
                          )}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                </RouterLink>
              ))}
            </div>
          )}
        </div>

        {/* Help Section */}
        <div className="mt-12 bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg p-6 sm:p-8 text-center">
          <h3 className="text-xl sm:text-2xl font-semibold text-charcoal-800 mb-4">Need Something Specific?</h3>
          <p className="text-charcoal-600 mb-6">
            Can't find what you're looking for? Kate and the team are here to help you find the right resources for your wellness journey.
          </p>
          <a
            href="mailto:hello@alera.co.nz?subject=Wellness Hub Support Request"
            className="inline-block bg-purple-600 text-white px-8 py-3 rounded-lg hover:bg-purple-700 transition-colors font-medium min-h-[44px]"
          >
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
};

export default ResourceHub;
